import Cookies from "js-cookie";
import useSWR from "swr";

export interface CashAccount {
    name: string;
    code: string;
    balance: number;
}

export interface CashPositionData {
    connected: boolean;
    message?: string;
    error?: string;
    totalCash?: number;
    accounts?: CashAccount[];
    asAt?: string;
}

export interface BiggestDay {
    date: string;
    amount: number;
}

export interface DailyTrendItem {
    date: string;
    inflow: number;
    outflow: number;
    net: number;
}

export interface CashMovementData {
    connected: boolean;
    message?: string;
    error?: string;
    inflow?: number;
    outflow?: number;
    netMovement?: number;
    biggestInflowDay?: BiggestDay | null;
    biggestOutflowDay?: BiggestDay | null;
    dailyTrend?: DailyTrendItem[];
    from?: string;
    to?: string;
}

// ── 13-Week Forecast Types ──────────────────────────────────────

export interface ForecastCategoryAmounts {
    inflow: number;
    outflow: number;
}

export interface CellDerivation {
    basis: "weeklyAvg" | "arScheduled" | "apScheduled" | "basCycle" | "basCycleNoDue";
    avgInflow: number;
    avgOutflow: number;
    scheduledAR?: number;
    scheduledAP?: number;
    repeatingInflow?: number;
    repeatingOutflow?: number;
    basAmount?: number;
}

export interface ForecastWeek {
    weekNumber: number;
    weekStarting: string;
    weekEnding: string;
    confidence: "high" | "medium" | "low";
    categories: Record<string, ForecastCategoryAmounts>;
    derivations?: Record<string, CellDerivation>;
    totalInflow: number;
    totalOutflow: number;
    netCashFlow: number;
    cumulativeCash: number;
    beginningCash?: number;
    endingCash?: number;
    status?: "forecast";
}

export interface ForecastSnapshot {
    snapshotId: string;
    capturedAt: string;
    totalInflow: number;
    totalOutflow: number;
    netCashFlow: number;
    beginningCash: number;
    endingCash: number;
    categories: Record<string, ForecastCategoryAmounts>;
}

export interface VarianceData {
    inflowDelta: number;
    inflowPct: number | null;
    outflowDelta: number;
    outflowPct: number | null;
    netDelta: number;
    netPct: number | null;
}

export interface TieOutResult {
    status: "pass" | "fail";
    failures: Array<Record<string, unknown>>;
}

export interface TieOutData {
    formulaTie: TieOutResult;
    chainTie: TieOutResult;
    bankAnchor: { status: "pass" | "fail"; detail: Record<string, unknown> };
}

export interface BridgeItem {
    weekNumber: number;
    arCollections: number;
    apPayments: number;
    basGst: number;
    repeatingInflow: number;
    repeatingOutflow: number;
}

export interface ActualWeek {
    weekNumber: number;
    weekStarting: string;
    weekEnding: string;
    beginningCash: number;
    inflow: number;
    outflow: number;
    net: number;
    endingCash: number;
    xeroOpening?: number | null;
    xeroClosing?: number | null;
    status: "actual";
    partial?: boolean;
    categories?: Record<string, ForecastCategoryAmounts>;
    forecastSnapshot?: ForecastSnapshot | null;
    variance?: VarianceData | null;
}

export interface BurnRateMetrics {
    weeklyAvg: number;
    runwayWeeks: number | null;
    minCashWeek: number | null;
    minCashAmount: number;
    cashNegativeWeek: number | null;
}

export interface ScenarioResult {
    endCash: number;
    runwayWeeks: number | null;
}

export interface ForecastAlert {
    severity: "critical" | "warning" | "info";
    message: string;
    weekNumber: number | null;
}

export interface WeeklyOverride {
    field: "inflow" | "outflow";
    value: number;
    original: number;
    op_id: string;
    label?: string;
}

export interface ARRollforwardWeek {
    weekNumber: number;
    openingAR: number;
    invoiced: number;
    collected: number;
    closingAR: number;
    impliedDSO: number | null;
}

export interface APRollforwardWeek {
    weekNumber: number;
    openingAP: number;
    billed: number;
    paid: number;
    closingAP: number;
    impliedDPO: number | null;
}

export interface ConcentrationCustomer {
    contactId: string;
    name: string;
    outstandingAR: number;
    shareOfTotalAR: number;
    avgDaysToPay: number | null;
    invoiceCount: number;
    isControlBucket?: boolean;
}

export interface ConcentrationRisk {
    topCustomers: ConcentrationCustomer[];
    totalAR: number;
    concentrationAlert: string | null;
}

export interface ControlReconciliationResult {
    action: string;
    controlTotal: number | null;
    detailTotalBefore: number;
    detailTotalAfter: number;
    delta: number;
}

export interface CashForecastData {
    connected: boolean;
    message?: string;
    error?: string;
    generatedAt?: string;
    currentCash?: number;
    weeks?: ForecastWeek[];
    actuals?: ActualWeek[];
    burnRate?: BurnRateMetrics;
    scenarios?: {
        base: ScenarioResult;
        optimistic: ScenarioResult;
        pessimistic: ScenarioResult;
    };
    alerts?: ForecastAlert[];
    weeklyOverrides?: Record<string, Record<string, WeeklyOverride>>;
    tieOut?: TieOutData;
    bridge?: BridgeItem[];
    collectionsCurve?: number[];
    arRollforward?: ARRollforwardWeek[];
    apRollforward?: APRollforwardWeek[];
    concentrationRisk?: ConcentrationRisk;
    lastSyncedAt?: string | null;
    cashAnchorPeriod?: string | null;
    cashAnchorSynced?: string | null;
    balanceSheetControlPeriod?: string | null;
    balanceSheetControlSynced?: string | null;
    controlReconciliation?: {
        ar?: ControlReconciliationResult | null;
        ap?: ControlReconciliationResult | null;
    };
}

export interface CashForecastOverrideInput {
    workspaceId?: string;
    scenarioId?: string;
    weekNumber: number;
    categoryKey: string;
    field: "inflow" | "outflow";
    value: number | null;
    label?: string;
    opId?: string;
}

// ── Shared constants ─────────────────────────────────────────

export const CATEGORY_LABELS: Record<string, string> = {
    customerReceipts: "Customer Receipts",
    stripeOnlineReceipts: "Stripe / Online Receipts",
    otherOperatingReceipts: "Other Operating Receipts",
    loanDrawdowns: "Loan Drawdowns",
    equityInjections: "Equity Injections",
    supplierPayments: "Supplier Payments",
    payrollNetWages: "Payroll (Net Wages)",
    superannuation: "Superannuation",
    payrollTax: "Payroll Tax",
    gstBasPayments: "GST / BAS Payments",
    contractors: "Contractors",
    softwareSaaSSubscriptions: "Software / SaaS",
    hostingCloudInfrastructure: "Hosting / Cloud",
    rentOccupancy: "Rent & Occupancy",
    marketingAgenciesPaidMedia: "Marketing",
    professionalFees: "Professional Fees",
    bankFeesFinanceCharges: "Bank Fees",
    capexEquipmentFixedAssets: "Capex / Equipment",
    capitalisedSoftware: "Capitalised Software",
    loanRepayments: "Loan Repayments",
    interestPayments: "Interest Payments",
    dividendsOwnerDraws: "Dividends / Owner Draws",
    otherOperatingOutflow: "Other Operating Outflow",
    otherUnclassified: "Other / Unclassified",
    unclassifiedCashReceipts: "Unclassified Cash Receipts / Bank Summary",
    unclassifiedCashPayments: "Other / Unclassified Cash Payment",
};

export const CONFIDENCE_STYLES: Record<string, string> = {
    high: "bg-green-100 text-green-800 border-green-200",
    medium: "bg-amber-100 text-amber-800 border-amber-200",
    low: "bg-gray-100 text-gray-600 border-gray-200",
};

const fetcher = async <T>(url: string): Promise<T> => {
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
        credentials: "include",
    });

    if (!response.ok) {
        if (response.status === 401) {
            return {connected: false, message: "Please log in to view cashflow data."} as T;
        }
        throw new Error(`Failed to fetch cashflow data: ${response.status}`);
    }

    return response.json();
};

export function useCashPosition(asAt?: string) {
    const params = asAt ? `?asAt=${asAt}` : "";
    const {data, error, isLoading, mutate} = useSWR<CashPositionData>(
        `/api/cash/position/${params}`,
        fetcher<CashPositionData>,
        {
            
            revalidateOnReconnect: true,
            dedupingInterval: 30000,
            errorRetryCount: 3,
        }
    );

    return {
        data,
        isLoading,
        error: error ? (error instanceof Error ? error.message : "Failed to fetch cash position") : null,
        refresh: mutate,
    };
}

export function useCashMovement(from?: string, to?: string) {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString() ? `?${params.toString()}` : "";

    const {data, error, isLoading, mutate} = useSWR<CashMovementData>(
        `/api/cash/movement/${qs}`,
        fetcher<CashMovementData>,
        {
            
            revalidateOnReconnect: true,
            dedupingInterval: 30000,
            errorRetryCount: 3,
        }
    );

    return {
        data,
        isLoading,
        error: error ? (error instanceof Error ? error.message : "Failed to fetch cash movement") : null,
        refresh: mutate,
    };
}

export function useCashForecast() {
    const {data, error, isLoading, mutate} = useSWR<CashForecastData>(
        "/api/cash/forecast/",
        fetcher<CashForecastData>,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            dedupingInterval: 120000,
            errorRetryCount: 2,
        }
    );

    return {
        data,
        isLoading,
        error: error ? (error instanceof Error ? error.message : "Failed to fetch forecast") : null,
        refresh: mutate,
    };
}

export function useCashForecastScoped(workspaceId?: string, scenarioId?: string) {
    const params = new URLSearchParams();
    if (workspaceId) params.set("workspace_id", workspaceId);
    if (scenarioId) params.set("scenario_id", scenarioId);
    const qs = params.toString();
    const {data, error, isLoading, mutate} = useSWR<CashForecastData>(
        `/api/cash/forecast/${qs ? `?${qs}` : ""}`,
        fetcher<CashForecastData>,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            dedupingInterval: 120000,
            errorRetryCount: 2,
        }
    );

    return {
        data,
        isLoading,
        error: error ? (error instanceof Error ? error.message : "Failed to fetch forecast") : null,
        refresh: mutate,
    };
}

async function mutateCashForecastOverride(
    method: "POST" | "DELETE",
    body: Record<string, unknown>,
): Promise<unknown> {
    const response = await fetch("/api/cash/forecast/override/", {
        method,
        headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": Cookies.get("csrftoken") || "",
        },
        credentials: "include",
        body: JSON.stringify(body),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const message = (data?.error as string) || (data?.errors?.[0] as string) || "Failed to save forecast override";
        throw new Error(message);
    }
    return data;
}

export async function saveCashForecastOverride(input: CashForecastOverrideInput) {
    return mutateCashForecastOverride("POST", {
        workspace_id: input.workspaceId,
        scenario_id: input.scenarioId,
        overrides: [{
            week_number: input.weekNumber,
            category_key: input.categoryKey,
            field: input.field,
            value: input.value,
            label: input.label,
        }],
    });
}

export async function clearCashForecastOverride(input: CashForecastOverrideInput) {
    if (input.opId) {
        return mutateCashForecastOverride("DELETE", {
            workspace_id: input.workspaceId,
            scenario_id: input.scenarioId,
            op_id: input.opId,
        });
    }
    return mutateCashForecastOverride("POST", {
        workspace_id: input.workspaceId,
        scenario_id: input.scenarioId,
        overrides: [{
            week_number: input.weekNumber,
            category_key: input.categoryKey,
            field: input.field,
            value: null,
        }],
    });
}
